import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { ReviewTerms } from '../client/components/ReviewTerms'; // Correct path
import StageC from '../components/StageC'; // Import our new StageC component
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../client/components/ui/card'; // Correct path
import { Button } from '../client/components/ui/button'; // Correct path
import { StageD } from '../components/StageD';
import { getApiUrl } from '../client/lib/config';

interface WorkflowStatus {
    status: string;
    files: string[];
    originalFilename: string;
    error?: string;
    reviewStage?: 'B' | 'C' | 'C1' | 'C2' | 'C3' | 'D';
    reviewData?: {
        suggestions: any[];
        uniqueTerms: string[];
    };
}

function WorkflowPage() {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState<WorkflowStatus | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    // Track time of first network error to determine persistence
    const [firstNetworkErrorTime, setFirstNetworkErrorTime] = useState<number | null>(null);
    const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const fetchStatus = useCallback(async (id: string) => {
        try {
            const response = await fetch(getApiUrl(`/api/workflow/${id}/status`));
            console.log(`[WorkflowPage] Response status: ${response.status}, ok: ${response.ok}`);
            const contentType = response.headers.get('content-type');
            console.log(`[WorkflowPage] Response Content-Type: ${contentType}`);

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Workflow not found. It might still be initializing or an error occurred.');
                }
                let errorText = response.statusText;
                try {
                    const rText = await response.text();
                    console.error('[WorkflowPage] Error response text:', rText);
                    errorText = `${response.statusText} - ${rText}`;
                } catch (textErr) {
                    console.error('[WorkflowPage] Could not get error response text:', textErr);
                }
                throw new Error(`Failed to fetch status: ${errorText}`);
            }

            // Check content type before parsing
            if (!contentType || !contentType.includes('application/json')) {
                const responseBodyText = await response.text();
                console.error('[WorkflowPage] Unexpected Content-Type. Response body:', responseBodyText);
                throw new Error(`Expected application/json but got ${contentType}. Response body: ${responseBodyText}`);
            }
            
            const data: WorkflowStatus = await response.json();
            setStatus(data);
            setIsLoading(false);
            setError(null);
            setFirstNetworkErrorTime(null); // Reset network error tracking on success

            // Stop polling if workflow completed or failed or pending review
            if (data.status === 'Completed' || data.status === 'Failed' || data.status.startsWith('Pending Review')) {
                if (pollingIntervalId) {
                    clearInterval(pollingIntervalId);
                    setPollingIntervalId(null);
                }
            }
        } catch (err: any) {
            console.error("[WorkflowPage] fetchStatus caught error object:", err); 
            console.error("[WorkflowPage] fetchStatus error name:", err.name);
            console.error("[WorkflowPage] fetchStatus error message:", err.message);
            
            // Check if this is a network error
            const isNetworkError = err.message === 'Failed to fetch' || 
                                   err.name === 'TypeError' || 
                                   err.message.includes('NetworkError');
            
            if (isNetworkError) {
                const now = Date.now();
                
                // If this is the first network error, record the time
                if (firstNetworkErrorTime === null) {
                    setFirstNetworkErrorTime(now);
                    // Don't show any error message yet
                } else {
                    // Only show error if network issues persist for more than 10 seconds
                    const errorDuration = now - firstNetworkErrorTime;
                    
                    // If we're in a review stage, be more forgiving with network errors
                    const isInReviewStage = status && status.status && status.status.startsWith('Pending Review');
                    const errorThreshold = isInReviewStage ? 20000 : 10000; // 20s for review stages, 10s otherwise
                    
                    if (errorDuration > errorThreshold) {
                        // If we're in Stage B review, provide a more specific message
                        if (status && status.status === 'Pending Review B') {
                            setError("Network connection issue during term review. Try refreshing the page if this persists.");
                        } else {
                            setError("Network connection issue. The server is busy processing your document.");
                        }
                        
                        // For Stage B specifically, try one immediate retry
                        if (status && status.status === 'Pending Review B') {
                            console.log("Attempting immediate retry for Stage B...");
                            // Short timeout to let any pending operations complete
                            setTimeout(() => fetchStatus(id), 500);
                        }
                    }
                }
                
                // Don't stop polling for network errors - they might be temporary
                return;
            }
            
            // For non-network errors, show immediately and stop polling
            setError(err.message);      
            setIsLoading(false);
            if (pollingIntervalId) {
                clearInterval(pollingIntervalId);
                setPollingIntervalId(null);
            }
        }
    }, [pollingIntervalId, firstNetworkErrorTime]);

    useEffect(() => {
        if (!workflowId) {
            setError("No workflow ID provided.");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        fetchStatus(workflowId); // Initial fetch

        // Start polling
        const intervalId = setInterval(() => fetchStatus(workflowId), 3000); // Poll every 3 seconds
        setPollingIntervalId(intervalId);

        // Cleanup function to stop polling when component unmounts
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [workflowId, fetchStatus]); // fetchStatus is memoized by useCallback

     // Callback for review components to signal completion
     const handleReviewComplete = () => {
         console.log("Review complete signal received, restarting polling...");
         setError(null); // Clear any previous errors
         setIsLoading(true); // Show loading indicator
         setFirstNetworkErrorTime(null); // Reset network error tracking
         if (workflowId) {
             // Clear any existing interval before starting a new one
             if (pollingIntervalId) {
                 clearInterval(pollingIntervalId);
             }
             // Fetch status immediately
             fetchStatus(workflowId);
             // Restart polling
             const intervalId = setInterval(() => fetchStatus(workflowId), 3000);
             setPollingIntervalId(intervalId);
         }
     };

    // Convert technical status to user-friendly descriptions
    const getDisplayStatus = (currentStatus: string, reviewStage?: string): string => {
        if (currentStatus.startsWith('Pending Review') && reviewStage) {
            switch (reviewStage) {
                case 'B':
                    return 'Review Terms';
                case 'C':
                case 'C1':
                case 'C2':
                    return 'Analyze Term Relationships';
                case 'C3':
                case 'D':
                    return 'Review Final Index and Subentries';
                default:
                    return 'Review Terms';
            }
        }
        
        switch (currentStatus) {
            case 'Completed':
                return 'Completed';
            case 'Failed':
                return 'Error';
            default:
                return 'Starting Index';
        }
    };

    const renderStatusIcon = (currentStatus: string) => {
        if (currentStatus.startsWith('Pending Review')) return <AlertCircle className="h-5 w-5 text-orange-500" />;
        if (currentStatus === 'Completed') return <CheckCircle2 className="h-5 w-5 text-green-600" />;
        if (currentStatus === 'Failed') return <AlertCircle className="h-5 w-5 text-destructive" />;
        return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    };

    // Determine if we need to show processing status instead of error
    const shouldShowProcessingStatus = () => {
        // If we have a status and it's neither completed nor failed, we're actively processing
        return status && !['Completed', 'Failed'].includes(status.status) && !status.status.startsWith('Pending Review');
    };

    // Add a robust submission handler function
    const handleStageSubmission = async (
        stage: 'B' | 'C' | 'D',
        payload: any,
        maxRetries = 3
    ) => {
        setIsSubmitting(true);
        setError(null);
        
        let retryCount = 0;
        let lastError = null;
        
        while (retryCount < maxRetries) {
            try {
                console.log(`Submitting stage ${stage} data, attempt ${retryCount + 1}/${maxRetries}`);
                
                // Use a timeout for the fetch operation
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 30000);
                
                const response = await fetch(getApiUrl(`/api/workflow/${workflowId}/submit-review/${stage}`), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(payload),
                    signal: controller.signal
                }).catch(fetchError => {
                    clearTimeout(timeoutId);
                    throw fetchError;
                });
                
                clearTimeout(timeoutId);
                
                if (!response.ok) {
                    const responseBody = await response.text();
                    console.error(`Error response from Stage ${stage} submission:`, responseBody);
                    
                    const contentType = response.headers.get('content-type');
                    let errorMessage = `Server error (${response.status})`;
                    
                    if (contentType && contentType.includes('application/json')) {
                        try {
                            const errorData = JSON.parse(responseBody);
                            errorMessage = errorData.error || errorMessage;
                        } catch (e) {
                            errorMessage = responseBody || errorMessage;
                        }
                    } else {
                        errorMessage = responseBody || errorMessage;
                    }
                    
                    throw new Error(errorMessage);
                }
                
                console.log(`Successfully submitted Stage ${stage} review`);
                handleReviewComplete(); // Restart polling on success
                
                // Show success message (but less obtrusive)
                console.log(`Stage ${stage} review submitted successfully`);
                return true;
            } catch (err) {
                retryCount++;
                lastError = err;
                
                // Only log the error, don't interrupt the user
                console.error(`Stage ${stage} submission error (attempt ${retryCount}/${maxRetries}):`, err);
                
                if (retryCount < maxRetries) {
                    // Wait before retrying - exponential backoff
                    const waitTime = 2000 * Math.pow(2, retryCount - 1); // 2s, 4s, 8s...
                    console.log(`Retrying in ${waitTime/1000} seconds...`);
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                }
            }
        }
        
        // If we get here, all retries failed
        if (lastError) {
            // If it's just a network error, we might still have succeeded, so check status
            const errorObj = lastError as Error;
            if (errorObj.message?.includes('network') || errorObj.name === 'AbortError') {
                console.log("Network error during submission, checking workflow status anyway...");
                handleReviewComplete();
                
                // Don't show error to user, just a warning in console
                console.warn("Continuing despite network error; the submission may have succeeded");
                setIsSubmitting(false);
                return true;
            } else {
                setError(`Submission failed after ${maxRetries} attempts: ${errorObj.message || 'Unknown error'}`);
            }
        } else {
            setError(`Submission failed after ${maxRetries} attempts`);
        }
        
        // Still restart polling even after error - the submission might have succeeded
        setTimeout(() => {
            handleReviewComplete();
        }, 3000);
        
        setIsSubmitting(false);
        return false;
    };

    return (
        <div className="min-h-screen bg-background">
            
            <div className="container mx-auto p-6 max-w-4xl">
                <Card className="bg-card border border-border/30">
                    <CardHeader className="pb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-muted/20 p-2 rounded-lg">
                                <FileText className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-semibold text-foreground">
                                    Workflow Status
                                </CardTitle>
                                <CardDescription className="text-muted-foreground text-sm">
                                    {status?.originalFilename || workflowId}
                    </CardDescription>
                            </div>
                        </div>
                        
                        {/* Workflow ID display */}
                        <div className="bg-muted/10 px-3 py-2 rounded border border-border/30 inline-block">
                            <span className="text-xs text-muted-foreground">ID:</span>
                            <span className="text-xs font-mono text-foreground ml-2">{workflowId}</span>
                        </div>
                </CardHeader>
                <CardContent>
                        {isLoading && !status && (
                            <div className="text-center py-6">
                                <div className="mb-3">
                                    <Loader2 className="w-6 h-6 text-muted-foreground animate-spin mx-auto" />
                                </div>
                                <p className="text-sm text-muted-foreground">Loading workflow status...</p>
                            </div>
                        )}
                        {error && !shouldShowProcessingStatus() && (
                            <div className="flex items-center gap-3 p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive mb-4">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}
                    
                    {status && (
                        <div className="space-y-6">
                            {/* Status Display */}
                            <div className="glass-card p-6 rounded-xl border-border/50">
                                <div className="flex items-center gap-3 mb-2">
                                 {renderStatusIcon(status.status)}
                                    <span className="text-xl font-semibold text-foreground">{getDisplayStatus(status.status, status.reviewStage)}</span>
                                </div>
                                
                                {/* Status indicator bar */}
                                <div className="w-full bg-muted/20 rounded-full h-2 mt-4">
                                    <div 
                                        className={`h-2 rounded-full transition-all duration-300 ${
                                            status.status === 'Completed' ? 'bg-green-600 w-full' :
                                            status.status === 'Failed' ? 'bg-destructive w-full' :
                                            status.status.startsWith('Pending Review') ? 'bg-orange-500 w-3/4' :
                                            'bg-primary w-1/2'
                                        }`}
                                    />
                                </div>
                             </div>
                            
                            {status.error && (
                                <div className="flex items-center gap-3 p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium">Backend Error Details:</p>
                                        <p className="text-sm mt-1">{status.error}</p>
                                    </div>
                                </div>
                            )}

                             {/* Stage B Review UI */}
                             {status.status === 'Pending Review B' && workflowId && status.reviewStage === 'B' && (
                                 <ReviewTerms
                                     workflowId={workflowId}
                                     stage='B'
                                     onSubmitReview={async (reviewDecisions) => {
                                         console.log(`Processing ${(reviewDecisions as any[]).length} decisions for Stage B`);
                                         
                                         // Convert to the format our handler expects
                                         try {
                                             await handleStageSubmission('B', reviewDecisions);
                                         } catch (err) {
                                             console.error("Failed to submit Stage B review:", err);
                                         }
                                     }}
                                 />
                             )}
 
                             {/* Stage C Review UI */}
                             {(status.status === 'Pending Review C' || status.status === 'Pending Review C1' || status.status === 'Pending Review C2' || status.status === 'Pending Review C3') && workflowId && (status.reviewStage === 'C' || status.reviewStage === 'C1' || status.reviewStage === 'C2' || status.reviewStage === 'C3') && (
                                 <>
                                 <div className="mb-4">
                                   <StageC 
                                     suggestions={status.reviewData?.suggestions || []}
                                     uniqueTerms={status.reviewData?.uniqueTerms || []}
                                     stage={status.reviewStage as 'C1' | 'C2' | 'C3' || 'C1'}
                                     onSubmit={async (decisions) => {
                                         console.log(`Processing ${decisions.length} decisions for Stage ${status.reviewStage}`);
                                         // Map C1/C2/C3 to 'C' for handleStageSubmission
                                         const stageForSubmission = status.reviewStage?.startsWith('C') ? 'C' : (status.reviewStage || 'C');
                                         await handleStageSubmission(stageForSubmission as 'B' | 'C' | 'D', { decisions });
                                     }}
                                   />
                                 </div>
                                 
                                 {/* Stage C Retry Button */}
                                 <Card className="mt-4 border-yellow-200">
                                     <CardHeader>
                                         <CardTitle className="text-yellow-700 text-sm">Having Trouble with Stage C?</CardTitle>
                                     </CardHeader>
                                     <CardContent>
                                         <Button 
                                             variant="outline"
                                             className="bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-300"
                                             onClick={() => {
                                                 setIsLoading(true);
                                                 setError(null);
                                                 fetchStatus(workflowId as string);
                                             }}
                                         >
                                             Reload Stage C Data
                                         </Button>
                                         <p className="text-xs text-slate-500 mt-2">
                                             Click this button if the review panel isn't loading properly
                                         </p>
                                     </CardContent>
                                 </Card>
                                 
                                 {/* EMERGENCY BYPASS BUTTON */}
                                 <Card className="mt-4 border-red-500">
                                     <CardHeader>
                                         <CardTitle className="text-red-500">Emergency Options</CardTitle>
                                         <CardDescription>Use only if experiencing persistent errors with Stage C</CardDescription>
                                     </CardHeader>
                                     <CardContent>
                                         <Button 
                                             variant="outline"
                                             className="bg-red-100 text-red-700 hover:bg-red-200 border-red-300"
                                             onClick={async () => {
                                                 if (!workflowId) return;
                                                 if (!window.confirm('Are you sure you want to bypass Stage C completely? This cannot be undone.')) return;
                                                 
                                                 setIsSubmitting(true);
                                                 setError(null);
                                                 try {
                                                     const response = await fetch(getApiUrl(`/api/workflow/${workflowId}/emergency-advance`), {
                                                         method: 'POST',
                                                         headers: { 'Content-Type': 'application/json' },
                                                         body: JSON.stringify({ stageToBypass: 'C' }),
                                                     });
                                                     
                                                     if (!response.ok) {
                                                         const errorData = await response.json();
                                                         throw new Error(errorData.error || 'Emergency bypass failed');
                                                     }
                                                     
                                                     handleReviewComplete();
                                                 } catch (err: any) {
                                                     setError(`Emergency bypass failed: ${err.message}`);
                                                 } finally {
                                                     setIsSubmitting(false);
                                                 }
                                             }}
                                             disabled={isSubmitting}
                                         >
                                             {isSubmitting ? 'Processing...' : 'Emergency: Skip Stage C'}
                                         </Button>
                                     </CardContent>
                                 </Card>
                                 </>
                             )}
 
                             {/* Stage D Review UI - DISABLED: Using old interface */}
                             {/* {status.status === 'Pending Review D' && workflowId && status.reviewStage === 'D' && (
                                 <>
                                 <div className="mb-4">
                                     <StageD 
                                         suggestions={status.reviewData?.suggestions || []}
                                         uniqueTerms={status.reviewData?.uniqueTerms || []}
                                         onSubmit={async (decisions) => {
                                             console.log(`Processing ${decisions.length} decisions for Stage D`);
                                             await handleStageSubmission('D', { decisions });
                                         }}
                                     />
                                 </div>
                                 </>
                             )} */}

                             {/* TODO: Add UI for other potential review stages */} 

                             {/* Generated Files List - Only show if not pending review and not failed */} 
                              {(status.status !== 'Failed' && !status.status.startsWith('Pending Review') && status.files.length > 0) && (
                                 <div>
                                     <h3 className="text-sm font-medium text-foreground mb-2">Generated Files:</h3>
                                     <ul className="list-disc pl-5 space-y-1">
                                         {status.files.map((file, index) => (
                                             <li key={index} className="text-sm">
                                                 <a 
                                                     href={getApiUrl(`/uploads/${workflowId}/${encodeURIComponent(file)}`)} 
                                                     download
                                                     target="_blank" // Open in new tab might be better than direct download sometimes
                                                     rel="noopener noreferrer"
                                                    className="text-primary hover:text-primary/80 hover:underline flex items-center transition-colors"
                                                 >
                                                     <FileText className="h-4 w-4 mr-1" />
                                                     {file}
                                                 </a>
                                             </li>
                                         ))}
                                     </ul>
                                 </div>
                             )}
                        </div>
                    )}
                </CardContent>
            </Card>
                
                <div className="mt-6 flex justify-center">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/')} 
                        className="text-muted-foreground hover:text-foreground transition-colors px-6"
                    >
                 Back to Upload
             </Button>
                </div>
            </div>
        </div>
    );
}

export default WorkflowPage; 